import React, { useState, useEffect } from "react";
import * as S from "./PlansStyle";
import { companyReceivingAccountService as accountService } from "../../Services/companyReceivingAccountService";
import {
  FaPlus,
  FaEdit,
  FaSpinner,
  FaExclamationTriangle,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";

const EMPTY_FORM = {
  beneficiary: "",
  beneficiaryCnpj: "",
  bank: "",
  agency: "",
  accountNumber: "",
  pixKey: "",
  pixKeyType: "",
};

export default function ContasDeposito() {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await accountService.getAll();
      setAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erro ao carregar contas de depósito:", err);
      alert("Não foi possível carregar as contas de depósito.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (account) => {
    setEditingId(account.id);
    setForm({
      beneficiary: account.beneficiary || "",
      beneficiaryCnpj: account.beneficiaryCnpj || "",
      bank: account.bank || "",
      agency: account.agency || "",
      accountNumber: account.accountNumber || "",
      pixKey: account.pixKey || "",
      pixKeyType: account.pixKeyType || "",
    });
    setIsModalOpen(true);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.beneficiary.trim() || !form.beneficiaryCnpj.trim() || !form.bank.trim()) {
      alert("Beneficiário, CNPJ e Banco são obrigatórios.");
      return;
    }

    const payload = {
      ...form,
      // envia null em vez de string vazia para o tipo da chave
      pixKeyType: form.pixKeyType || null,
    };

    setIsSaving(true);
    try {
      if (editingId) {
        await accountService.update(editingId, payload);
      } else {
        await accountService.create(payload);
      }
      setIsModalOpen(false);
      await fetchData();
    } catch (err) {
      alert("Erro ao salvar: " + (err.response?.data || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (account) => {
    const acao = account.isActive ? "desativar" : "ativar";
    if (!window.confirm(`Tem certeza que deseja ${acao} a conta de ${account.beneficiary}?`)) {
      return;
    }

    setTogglingId(account.id);
    try {
      await accountService.setActive(account.id, !account.isActive);
      await fetchData();
    } catch (err) {
      alert("Erro ao alterar status: " + (err.response?.data || err.message));
    } finally {
      setTogglingId(null);
    }
  };

  const labelForPixType = (type) =>
    type ? accountService.PixKeyTypeLabels[type] || type : "-";

  return (
    <>
      <S.Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <div>
            <h3>Contas de Depósito</h3>
            <p
              style={{
                color: "#888",
                fontSize: "0.85rem",
                margin: "5px 0 0 15px",
              }}
            >
              Contas bancárias/PIX de recebimento da empresa.
            </p>
          </div>
          <S.AddButton onClick={handleOpenCreate}>
            <FaPlus /> Nova Conta
          </S.AddButton>
        </div>

        {loading ? (
          <S.EmptyState>
            <p style={{ color: "#B17D2A" }}>Carregando contas...</p>
          </S.EmptyState>
        ) : accounts.length === 0 ? (
          <S.EmptyState>
            <FaExclamationTriangle
              size={30}
              color="#B17D2A"
              style={{ marginBottom: "10px" }}
            />
            <p>Nenhuma conta cadastrada.</p>
          </S.EmptyState>
        ) : (
          <S.Table>
            <thead>
              <tr>
                <th>Beneficiário</th>
                <th>Banco</th>
                <th>Ag. / Conta</th>
                <th>Chave PIX</th>
                <th style={{ textAlign: "center" }}>Status</th>
                <th style={{ textAlign: "center" }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{account.beneficiary}</div>
                    <div style={{ color: "#888", fontSize: "0.8rem" }}>
                      {account.beneficiaryCnpj}
                    </div>
                  </td>
                  <td>{account.bank}</td>
                  <td>
                    {account.agency || "-"} / {account.accountNumber || "-"}
                  </td>
                  <td>
                    <div>{account.pixKey || "-"}</div>
                    <div style={{ color: "#888", fontSize: "0.8rem" }}>
                      {labelForPixType(account.pixKeyType)}
                    </div>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <S.StatusBadge $active={account.isActive}>
                      {account.isActive ? "Ativa" : "Inativa"}
                    </S.StatusBadge>
                  </td>
                  <td>
                    <S.ActionButtonsCell>
                      <S.IconButton
                        onClick={() => handleOpenEdit(account)}
                        title="Editar"
                      >
                        <FaEdit />
                      </S.IconButton>
                      <S.IconButton
                        onClick={() => handleToggleActive(account)}
                        disabled={togglingId === account.id}
                        title={account.isActive ? "Desativar" : "Ativar"}
                      >
                        {togglingId === account.id ? (
                          <S.SpinnerIcon>
                            <FaSpinner />
                          </S.SpinnerIcon>
                        ) : account.isActive ? (
                          <FaToggleOn />
                        ) : (
                          <FaToggleOff />
                        )}
                      </S.IconButton>
                    </S.ActionButtonsCell>
                  </td>
                </tr>
              ))}
            </tbody>
          </S.Table>
        )}
      </S.Card>

      {isModalOpen && (
        <S.ModalOverlay onClick={() => !isSaving && setIsModalOpen(false)}>
          <S.ModalContent onClick={(e) => e.stopPropagation()}>
            <h2>{editingId ? "Editar Conta" : "Nova Conta de Depósito"}</h2>
            <p>Dados bancários/PIX de recebimento da empresa.</p>

            <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
              <S.FormGroup>
                <label>Beneficiário *</label>
                <input
                  type="text"
                  value={form.beneficiary}
                  onChange={(e) => handleFieldChange("beneficiary", e.target.value)}
                  placeholder="Nome do beneficiário"
                  autoFocus
                />
              </S.FormGroup>

              <S.FormGroup>
                <label>CNPJ do Beneficiário *</label>
                <input
                  type="text"
                  value={form.beneficiaryCnpj}
                  onChange={(e) => handleFieldChange("beneficiaryCnpj", e.target.value)}
                  placeholder="00.000.000/0000-00"
                />
              </S.FormGroup>

              <S.FormGroup>
                <label>Banco *</label>
                <input
                  type="text"
                  value={form.bank}
                  onChange={(e) => handleFieldChange("bank", e.target.value)}
                  placeholder="Ex: Banco do Brasil"
                />
              </S.FormGroup>

              <S.FormGrid>
                <S.FormGroup>
                  <label>Agência</label>
                  <input
                    type="text"
                    value={form.agency}
                    onChange={(e) => handleFieldChange("agency", e.target.value)}
                    placeholder="Ex: 1234-5"
                  />
                </S.FormGroup>
                <S.FormGroup>
                  <label>Conta</label>
                  <input
                    type="text"
                    value={form.accountNumber}
                    onChange={(e) => handleFieldChange("accountNumber", e.target.value)}
                    placeholder="Ex: 12345-6"
                  />
                </S.FormGroup>
              </S.FormGrid>

              <S.FormGroup>
                <label>Chave PIX</label>
                <input
                  type="text"
                  value={form.pixKey}
                  onChange={(e) => handleFieldChange("pixKey", e.target.value)}
                  placeholder="Chave PIX"
                />
              </S.FormGroup>

              <S.FormGroup>
                <label>Tipo da Chave PIX</label>
                <select
                  value={form.pixKeyType}
                  onChange={(e) => handleFieldChange("pixKeyType", e.target.value)}
                >
                  <option value="">Selecione...</option>
                  <option value={accountService.PixKeyType.CNPJ}>CNPJ</option>
                  <option value={accountService.PixKeyType.EMAIL}>E-mail</option>
                  <option value={accountService.PixKeyType.CHAVE_ALEATORIA}>
                    Chave Aleatória
                  </option>
                  <option value={accountService.PixKeyType.TELEFONE}>Telefone</option>
                </select>
              </S.FormGroup>
            </div>

            <S.ModalFooter>
              <S.AddButton
                onClick={() => setIsModalOpen(false)}
                disabled={isSaving}
                style={{ border: "none" }}
              >
                Cancelar
              </S.AddButton>
              <S.SaveButton onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <S.SpinnerIcon>
                    <FaSpinner />
                  </S.SpinnerIcon>
                ) : (
                  "Salvar Conta"
                )}
              </S.SaveButton>
            </S.ModalFooter>
          </S.ModalContent>
        </S.ModalOverlay>
      )}
    </>
  );
}
